import { useTranslations } from "use-intl";

import { type SingleFightDetailsData } from "@/features/flights/contexts/SingleFlightDetailsContext";
import { extractAirlineCarriers } from "@/features/flights/utils/extractAirlineCarriers";
import { parseDuration } from "@/utils/duration";

import { AirlineAvatarGroup } from "../AirlineAvatarGroup";
import { BaggageAllowance } from "../BaggageAllowance";
import { FlightInfo, FlightOverallTime } from "../flights/FlightInfo";

function FlightSummary({
  selectedFlight,
}: {
  selectedFlight?: SingleFightDetailsData;
}) {
  const t = useTranslations();
  const departureFlight = selectedFlight?.flights?.[0] || null;
  const departureSegments = departureFlight?.segments || [];
  const departureStops = departureFlight?.stops || [];

  const returnFlight =
    ((selectedFlight?.flights?.length || 0) > 1 &&
      selectedFlight?.flights?.[selectedFlight?.flights.length - 1]) ||
    null;
  const returnSegments = returnFlight?.segments || [];
  const returnStops = returnFlight?.stops || [];

  const departureAirlines = extractAirlineCarriers(departureSegments) || [];
  const returnAirlines = extractAirlineCarriers(returnSegments) || [];

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full">
        <span className="text-base font-medium text-text-title">
          {t("flights.flightSummary")}
        </span>
      </div>
      <span className="section-title mt-6">{t("flights.flightDetails")}</span>
      <div className="section-card mb-2 flex flex-col gap-4">
        <div className="flex w-full items-center gap-2">
          <div className="flex w-fit">
            <AirlineAvatarGroup airlines={departureAirlines} />
          </div>
          <div>
            {departureAirlines?.length &&
              [...departureAirlines]
                ?.splice(0, 2)
                .map((airline, index, array) => {
                  const doesOperatingCarrierDiffer =
                    airline.code !== airline.operatingCarrier.code;

                  return (
                    <span
                      key={airline.name || index}
                      className="text-sm text-text-primary"
                    >
                      {airline.name || t("unknownAirline")}
                      {doesOperatingCarrierDiffer ? (
                        <>
                          {" "}
                          <span className="text-text-tertiary">
                            ({t("flights.flightOperatedBy")}{" "}
                            {airline.operatingCarrier.name ||
                              airline.operatingCarrier.code}
                            )
                          </span>
                        </>
                      ) : null}
                      {array.length > index + 1 ? ", " : ""}
                    </span>
                  );
                })}
            {departureAirlines?.length - 2 > 0 && (
              <span className="mr-1 text-sm text-text-primary">
                +{departureAirlines.length - 2}
              </span>
            )}
          </div>
        </div>

        <FlightInfo
          origin={{
            date: departureSegments[0].departure.date,
            time: departureSegments[0].departure.time,
            code: departureSegments[0].departure.airport.code,
          }}
          destination={{
            date: departureSegments[departureSegments.length - 1].arrival.date,
            time: departureSegments[departureSegments.length - 1].arrival.time,
            code: departureSegments[departureSegments.length - 1].arrival
              .airport.code,
          }}
        >
          <FlightOverallTime
            overallTime={t(
              "durationValue",
              departureFlight?.duration
                ? parseDuration(departureFlight.duration)
                : undefined,
            )}
            stops={departureStops?.length}
          />
        </FlightInfo>
      </div>
      <BaggageAllowance baggageData={selectedFlight?.outboundBaggage} />

      {/* Return flight if its round trip */}
      {returnFlight && (
        <>
          <div className="section-card mt-4 mb-2 flex flex-col gap-4">
            <div className="flex w-full items-center gap-2">
              <div className="flex w-fit">
                <AirlineAvatarGroup airlines={returnAirlines} />
              </div>
              <div>
                {returnAirlines?.length &&
                  [...returnAirlines]
                    ?.splice(0, 2)
                    .map((airline, index, array) => {
                      const doesOperatingCarrierDiffer =
                        airline.code !== airline.operatingCarrier.code;

                      return (
                        <span
                          key={airline.name || index}
                          className="text-sm text-text-primary"
                        >
                          {airline.name || t("unknownAirline")}
                          {doesOperatingCarrierDiffer ? (
                            <>
                              {" "}
                              <span className="text-text-tertiary">
                                ({t("flights.flightOperatedBy")}{" "}
                                {airline.operatingCarrier.name ||
                                  airline.operatingCarrier.code}
                                )
                              </span>
                            </>
                          ) : null}
                          {array.length > index + 1 ? ", " : ""}
                        </span>
                      );
                    })}

                {returnAirlines?.length - 2 > 0 && (
                  <span className="mr-1 text-sm text-text-primary">
                    +{returnAirlines.length - 2}
                  </span>
                )}
              </div>
            </div>

            <FlightInfo
              origin={{
                date: returnSegments[0].departure.date,
                time: returnSegments[0].departure.time,
                code: returnSegments[0].departure.airport.code,
              }}
              destination={{
                date: returnSegments[returnSegments.length - 1].arrival.date,
                time: returnSegments[returnSegments.length - 1].arrival.time,
                code: returnSegments[returnSegments.length - 1].arrival.airport
                  .code,
              }}
            >
              <FlightOverallTime
                overallTime={t(
                  "durationValue",
                  returnFlight?.duration
                    ? parseDuration(returnFlight.duration)
                    : undefined,
                )}
                stops={returnStops.length}
              />
            </FlightInfo>
          </div>
          {selectedFlight?.returnBaggage !== null ? (
            <BaggageAllowance baggageData={selectedFlight?.returnBaggage} />
          ) : null}
        </>
      )}
    </div>
  );
}

export default FlightSummary;
